import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Spinner } from "react-bootstrap";
import { ArrowLeft, RefreshCcw } from "lucide-react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getTrackTraceLocationMap } from "../trackTrace/api";
import { TrackTraceAsset, TrackTraceFilters, TrackTraceMapPayload, TrackTraceZone } from "../trackTrace/types";
import AssetBottomSheet from "../components/track-trace/AssetBottomSheet";
import AssetFilterPanel from "../components/track-trace/AssetFilterPanel";
import AssetSearchBar from "../components/track-trace/AssetSearchBar";
import FloorPlanCanvas from "../components/track-trace/FloorPlanCanvas";
import MapEmptyState from "../components/track-trace/MapEmptyState";
import MapStatusLegend from "../components/track-trace/MapStatusLegend";
import "../assets/styles/track-trace.css";

const cacheKey = (locationId?: string, mapId?: string | null) => `track-trace-map:${locationId}:${mapId || 'default'}`;

const TrackTraceMapPage: React.FC = () => {
    const { i18n } = useTranslation();
    const t = useCallback((key: string, options?: object) => (i18n.t as unknown as (key: string, options?: object) => string)(key, options), [i18n]);
    const { locationId } = useParams();
    const [searchParams] = useSearchParams();
    const mapId = searchParams.get('map_id');
    const initialAssetTypeId = searchParams.get('asset_type_id') || undefined;
    const navigate = useNavigate();
    const [payload, setPayload] = useState<TrackTraceMapPayload | null>(null);
    const [filters, setFilters] = useState<TrackTraceFilters>(initialAssetTypeId ? { asset_type_id: initialAssetTypeId } : {});
    const [search, setSearch] = useState('');
    const [selectedAsset, setSelectedAsset] = useState<TrackTraceAsset | null>(null);
    const [selectedZone, setSelectedZone] = useState<TrackTraceZone | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [usingCache, setUsingCache] = useState(false);
    const [imageError, setImageError] = useState(false);
    const [isBottomSheetCollapsed, setIsBottomSheetCollapsed] = useState(true);

    const load = useCallback(async (nextFilters: TrackTraceFilters = {}) => {
        if (!locationId) return;
        setLoading(true);
        setError('');
        try {
            const requestFilters = { ...nextFilters, map_id: mapId || undefined };
            const data = await getTrackTraceLocationMap(locationId, requestFilters);
            setPayload(data);
            setUsingCache(false);
            localStorage.setItem(cacheKey(locationId, mapId), JSON.stringify(data));
        } catch (err) {
            const cached = localStorage.getItem(cacheKey(locationId, mapId));
            if (cached) {
                setPayload(JSON.parse(cached));
                setUsingCache(true);
                setError(t('track_trace.map.errors.refresh_failed_using_cache'));
            } else {
                setError(t('track_trace.map.errors.load_failed'));
            }
        } finally {
            setLoading(false);
        }
    }, [locationId, mapId, t]);

    useEffect(() => {
        load(initialAssetTypeId ? { asset_type_id: initialAssetTypeId } : {});
    }, [load, initialAssetTypeId]);

    const selectedAssetId = selectedAsset?.id || null;
    const visiblePayload = useMemo(() => payload, [payload]);

    const applySearch = () => {
        const next = { ...filters, q: search || undefined };
        setFilters(next);
        load(next);
    };

    const applyFilters = () => load(filters);

    return (
        <div className="track-trace-map-page">
            <div className="map-topbar">
                <Button variant="light" size="sm" onClick={() => navigate('/track-trace/locations')} aria-label={t('track_trace.map.actions.back_to_locations')}>
                    <ArrowLeft size={18} />
                </Button>
                <div>
                    <h1>{payload?.location.name || t('track_trace.map.floor_map')}</h1>
                    <p>
                        {payload?.map_context?.name || payload?.floor_map?.name || (usingCache ? t('track_trace.map.last_saved_data') : t('track_trace.map.live_map_payload'))}
                        {payload?.location.path ? ` - ${payload.location.path}` : ''}
                    </p>
                </div>
                <Button variant="light" size="sm" onClick={() => load(filters)} aria-label={t('track_trace.map.actions.refresh_map')}>
                    <RefreshCcw size={18} />
                </Button>
            </div>

            <AssetSearchBar value={search} onChange={setSearch} onSubmit={applySearch} />
            <AssetFilterPanel payload={payload} filters={filters} onChange={setFilters} onApply={applyFilters} />
            <MapStatusLegend />

            {loading && <div className="track-trace-center"><Spinner animation="border" /></div>}
            {error && <Alert variant={usingCache ? 'warning' : 'danger'}>{error}</Alert>}
            {imageError && <Alert variant="warning">{t('track_trace.map.errors.image_load_failed')}</Alert>}

            {!loading && visiblePayload && (
                <>
                    {visiblePayload.floor_map ? (
                        <FloorPlanCanvas
                            payload={visiblePayload}
                            selectedAssetId={selectedAssetId}
                            selectedZoneId={selectedZone?.id || null}
                            onSelectAsset={(asset) => {
                                setSelectedAsset(asset);
                                setIsBottomSheetCollapsed(false);
                            }}
                            onSelectZone={setSelectedZone}
                            onImageError={() => setImageError(true)}
                        />
                    ) : (
                        <MapEmptyState message={t('track_trace.map.empty.no_floor_map')} />
                    )}
                    {visiblePayload.assets.length === 0 && <MapEmptyState message={t('track_trace.map.empty.no_matching_assets')} />}
                    <AssetBottomSheet
                        payload={visiblePayload}
                        selectedAsset={selectedAsset}
                        collapsed={isBottomSheetCollapsed}
                        onSelectAsset={(asset) => {
                            setSelectedAsset(asset);
                            setIsBottomSheetCollapsed(false);
                        }}
                        onClear={() => setSelectedAsset(null)}
                        onToggleCollapsed={(collapsed) => {
                            setIsBottomSheetCollapsed(collapsed);
                            if (collapsed) {
                                setSelectedAsset(null);
                            }
                        }}
                    />
                </>
            )}
        </div>
    );
};

export default TrackTraceMapPage;
